import {
  Building2,
  CheckCircle2,
  Loader2,
  MapPin,
  Phone,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useGetProviderDirectoryServiceQuery } from '@/services/api/providerDirectoryV2Service';
import { useResolveProviderIdentityMutation } from '@/services/api/treatmentService';
import type { MedicalProvider } from '@/services/types/treatment-types';
import { ProviderIdentityLifecycleStatus } from '@/services/types/treatment-types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IdentityCheckStepProps {
  provider: MedicalProvider;
  matterId: string;
  onAccept: () => void;
  onReject: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAddress(
  address?: {
    address_line1?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    formatted_address?: string | null;
  } | null,
): string | null {
  if (!address) return null;
  if (address.formatted_address) return address.formatted_address;
  const parts = [
    address.address_line1,
    address.city,
    address.state,
    address.postal_code,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const IdentityCheckStep = ({
  provider,
  matterId,
  onAccept,
  onReject,
}: IdentityCheckStepProps) => {
  const providerServiceId = provider.provider_service_ref_id ?? null;

  const { data: providerService, isLoading: isLoadingService } =
    useGetProviderDirectoryServiceQuery(
      { serviceId: providerServiceId ?? '' },
      { skip: !providerServiceId },
    );

  const [resolveIdentity, { isLoading: isResolving }] =
    useResolveProviderIdentityMutation();

  // Track local resolved state so the success banner shows immediately after
  // the mutation succeeds, without waiting for the async RTK cache refetch.
  const [justResolved, setJustResolved] = useState(false);
  const isResolved =
    justResolved ||
    provider.identity_status === ProviderIdentityLifecycleStatus.RESOLVED;

  // Directory entry details
  const directoryName = providerService?.name ?? null;
  const directoryOrg =
    providerService?.clinical_location?.organization?.name ?? null;
  const directoryAddress = formatAddress(
    providerService?.clinical_location?.address,
  );
  const directoryPhone =
    providerService?.clinical_location?.primary_phone ?? null;

  const handleAccept = async () => {
    try {
      await resolveIdentity({
        matterId,
        providerId: provider.id,
      }).unwrap();
      setJustResolved(true);
      toast.success('Identity verified');
      onAccept();
    } catch {
      toast.error('Failed to verify identity');
    }
  };

  return (
    <div className="space-y-4">
      {/* Explanation banner */}
      <div className="rounded-md border border-sky-200 bg-sky-50 p-3">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-sky-600" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Verify Provider Identity
            </p>
            <p className="mt-0.5 text-xs text-gray-600">
              Before creating a records request, confirm that this provider on
              the matter matches the correct entry in the directory. This
              ensures requests are sent to the right location.
            </p>
          </div>
        </div>
      </div>

      {/* Side-by-side cards */}
      {isLoadingService ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-600">
          <Loader2 className="size-4 animate-spin" />
          Loading directory details...
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {/* Provider on Matter card */}
          <div className="rounded-md border border-gray-200 p-3">
            <p className="text-xs font-medium text-gray-600">
              Provider on Matter
            </p>
            <div className="mt-2 space-y-2">
              <p className="text-sm font-medium text-gray-900">
                {provider.name ?? 'Unnamed Provider'}
              </p>
              {provider.address && (
                <div className="flex items-start gap-1.5">
                  <MapPin className="mt-0.5 size-3.5 shrink-0 text-gray-600" />
                  <span className="text-sm text-gray-600">
                    {provider.address}
                  </span>
                </div>
              )}
              {provider.provider_type && (
                <div className="flex items-start gap-1.5">
                  <Building2 className="mt-0.5 size-3.5 shrink-0 text-gray-600" />
                  <span className="text-sm text-gray-600">
                    {provider.provider_type}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Directory Entry card */}
          <div className="rounded-md border border-gray-200 p-3">
            <p className="text-xs font-medium text-gray-600">
              Directory Entry
            </p>
            <div className="mt-2 space-y-2">
              <p className="text-sm font-medium text-gray-900">
                {directoryName ?? 'Unknown Service'}
              </p>
              {directoryOrg && directoryOrg !== directoryName && (
                <div className="flex items-start gap-1.5">
                  <Building2 className="mt-0.5 size-3.5 shrink-0 text-gray-600" />
                  <span className="text-sm text-gray-600">{directoryOrg}</span>
                </div>
              )}
              {directoryAddress && (
                <div className="flex items-start gap-1.5">
                  <MapPin className="mt-0.5 size-3.5 shrink-0 text-gray-600" />
                  <span className="text-sm text-gray-600">
                    {directoryAddress}
                  </span>
                </div>
              )}
              {directoryPhone && (
                <div className="flex items-start gap-1.5">
                  <Phone className="mt-0.5 size-3.5 shrink-0 text-gray-600" />
                  <span className="text-sm text-gray-600">
                    {directoryPhone}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {!isResolved && (
        <div className="flex items-center gap-3 border-t border-gray-200 pt-3">
          <Button
            size="sm"
            onClick={handleAccept}
            disabled={isResolving || isLoadingService}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {isResolving ? (
              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-1.5 size-3.5" />
            )}
            Accept Identity
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={isResolving}
          >
            <XCircle className="mr-1.5 size-3.5" />
            Reject &amp; Re-link
          </Button>
        </div>
      )}

      {isResolved && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <CheckCircle2 className="size-4 text-emerald-600" />
          <span className="text-sm font-medium text-emerald-800">
            Identity verified &mdash; you may proceed.
          </span>
        </div>
      )}
    </div>
  );
};
